import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/home/runner/work/doctocat/doctocat/theme/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Doctocat can render code blocks as live previews with editable code using `}<a parentName="p" {...{
        "href": "https://github.com/FormidableLabs/react-live"
      }}>{`react-live`}</a>{`. To make a code block render as an editable example, add a `}<inlineCode parentName="p">{`live`}</inlineCode>{` attribute after the language:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`\`\`\`jsx live
<button>Hello, world!</button>
\`\`\`
`}</code></pre>
    <p>{`Here's how the above example will render on a page:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<button>Hello, world!</button>
`}</code></pre>
    <p>{`Doctocat supports live code examples for `}<inlineCode parentName="p">{`jsx`}</inlineCode>{` and `}<inlineCode parentName="p">{`html`}</inlineCode>{`.`}</p>
    <h2>{`Scope`}</h2>
    <p>{`Before you can use components or other variables in live code examples, you'll need to add them to the global scope of live previews. Otherwise, the live previews will display a `}<inlineCode parentName="p">{`ReferenceError`}</inlineCode>{`, like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`// \`Foo\` was not added to the global scope of live previews.
<Foo>Hello, world!</Foo>
`}</code></pre>
    <p>{`You can add variables to the global scope of live previews by `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/blog/2019-04-29-component-shadowing/"
      }}>{`shadowing`}</a>{` `}<inlineCode parentName="p">{`src/@primer/gatsby-theme-doctocat/live-code-scope.js`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// src/@primer/gatsby-theme-doctocat/live-code-scope.js
import {Box} from '@primer/react'

export default {
  Box,
  text: 'Hello, world!',
}
`}</code></pre>
    <Note variant="warning" mdxType="Note">
      <p>{`Make sure to place `}<inlineCode parentName="p">{`live-code-scope.js`}</inlineCode>{` in the root of your `}<inlineCode parentName="p">{`gatsby-theme-doctocat`}</inlineCode>{` folder. Shadowed files must be present in the same location as they exist in the theme repository.`}</p>
    </Note>
    <p>{`Every property on the object exported by `}<inlineCode parentName="p">{`live-code-scope.js`}</inlineCode>{` will be available as a variable in the code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`\`\`\`jsx live
<Box p={3}>{text}</Box>
\`\`\`
`}</code></pre>
    <h2>{`Line highlighting`}</h2>
    <p>{`If you want to emphasize a particular range of lines, use the `}<inlineCode parentName="p">{`highlight`}</inlineCode>{` attribute on the code block. The expected format is `}<inlineCode parentName="p">{`higlight=start-end`}</inlineCode>{`. The line highlighting will disappear when the live example is edited.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`\`\`\` jsx live highlight=2-4
<div>
  <p>
    This paragraph element should be higlighted.
  </p>
  <p>
    This paragraph element should not be higlighted.
  </p>
</div>
\`\`\`
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live highlight=2-4",
        "live": true,
        "highlight": "2-4"
      }}>{`<div>
  <p>
    This paragraph element should be higlighted.
  </p>
  <p>
    This paragraph element should not be higlighted.
  </p>
</div>
`}</code></pre>
    <h2>{`Global styles`}</h2>
    <p>{`Live previews are completely isolated from the rest of the page because they are rendered inside `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe"
      }}>{`iframes`}</a>{`. This means that you can apply global styles inside live previews without affecting the rest of the page:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<div>
  <style>{\`p { color: red; }\`}</style>
  <p>
    This text should be red. All other text on the page should be unaffected.
  </p>
</div>
`}</code></pre>
    <p>{`You can apply global styles to `}<em parentName="p">{`all`}</em>{` live previews on your site by `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/blog/2019-04-29-component-shadowing/"
      }}>{`shadowing`}</a>{` `}<inlineCode parentName="p">{`live-preview-wrapper.js`}</inlineCode>{` with a component that renders a `}<inlineCode parentName="p">{`style`}</inlineCode>{` or `}<inlineCode parentName="p">{`link`}</inlineCode>{` tag:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// src/@primer/gatsby-theme-doctocat/components/live-preview-wrapper.js
import React from 'react'

export default function LivePreviewWrapper({children}) {
  return (
    <div>
      <link href="https://unpkg.com/primer/build/build.css" rel="stylesheet" />
      {children}
    </div>
  )
}
`}</code></pre>
    <h2>{`No-inline`}</h2>
    <p>{`By default, live previews treat your code as a single JSX expression to be rendered. If you want to include more complex code, use the `}<inlineCode parentName="p">{`noinline`}</inlineCode>{` attribute on the code block, and render components with the exported `}<inlineCode parentName="p">{`render`}</inlineCode>{` function:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`\`\`\`javascript live noinline
function DemoApp() {
  const [count, setCount] = React.useState(0)

  return (
    <div>
      <button onClick={() => setCount((n) => n - 1)}>-</button>
      {count}
      <button onClick={() => setCount((n) => n + 1)}>+</button>
    </div>
  )
}

render(<DemoApp />)
\`\`\`
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`function DemoApp() {
  const [count, setCount] = React.useState(0)

  return (
    <div style={{textAlign: 'center'}}>
      <button onClick={() => setCount((n) => n - 1)}>-</button>
      {count}
      <button onClick={() => setCount((n) => n + 1)}>+</button>
    </div>
  )
}

render(<DemoApp />)
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      